import { Location } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, inject } from '@angular/core';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { LocalRepository } from '@shared/helper/local-repository';
import { BaseComponent } from '@shared/components/base/base.component';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { ShelvesAggrigatorService } from '@app/pages/library/services/shelvesAggrigator.service';
import { BookWithoutReviewModel } from '@shared/model/library/book-without-review.model';
import { GustShelvesWithBooksAggrigator } from '@app/pages/library/models/guest-shelves-with-book-aggrigator-model';
import { ShelfItemModel } from '@shared/model/customerModels/shelves-model';
import { htmlUrls } from '@shared/utils/router-paths';
import { Observable, of } from 'rxjs';
import { SharedModule } from "@shared/module/shared.module";
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BehkhaanBookCardComponent } from "@app/pages/books/components/behkhaan-book-card/behkhaan-book-card.component";
import { LibraryShelfComponent } from "@app/pages/library/components/library-shelf/library-shelf.component";
import { NewShelfSheetComponent } from "@app/pages/library/components/new-shelf-sheet/new-shelf-sheet.component";

import { SearchFilterModel } from '@shared/model/categoryAndSearch/search-filter-model';
import { TServiceResult } from '@shared/helper/t-service-result';
import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { ensureSwiperRegistered } from '@shared/loaders/swiper-loader';

@Component({
  selector: 'app-library',
  imports: [
    SharedModule,
    MatMenuModule,
    MatTooltipModule,
    LibraryShelfComponent,
    BehkhaanBookCardComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './library.component.html',
  styleUrls: ['./library.component.scss']
})
export class LibraryComponent extends BaseComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private shelvesAggrigatorService = inject(ShelvesAggrigatorService);
  _localRepository = inject(LocalRepository);
  private _dialog = inject(MatDialog);
  private _bottomSheet = inject(MatBottomSheet);
  _location = inject(Location);

  // ? Constants
  routes = htmlUrls;

  userShelvesSearchFilter: SearchFilterModel = new SearchFilterModel();
  userShelvesTermFilter: TermFilterModel = new TermFilterModel(0, 20);
  defaultBooksTermFilter: TermFilterModel = new TermFilterModel(0, 4);

  // ? User Info
  userName = '';
  userId = '';
  libraryOwner = false;

  userShelves: ShelfItemModel[] = [];
  booksWithoutReview$!: Observable<BookWithoutReviewModel[]>;
  defaultShelves$!: Observable<GustShelvesWithBooksAggrigator[]>;
  totalBooksCount = 0;

  // ? Loading Flags
  userHasMoreShelves = false;
  loading = true;
  loadingMoreShelves = false;

  // ? Display Options
  displaySearchBar = true;

  sortFilter = 'CreatedAt';
  filterDirection = 0;

  filterButtons = [
    {
      value: 'Title.Value',
      title: 'عنوان قفسه'
    },
    {
      value: 'CreatedAt',
      title: 'تاریخ افزودن'
    },
  ]

  constructor() {
    super();
    this.userShelvesSearchFilter.pgNumber = 0;
    this.userShelvesSearchFilter.pgSize = 20;
    this.sortFilter = 'CreatedAt';
  }

  async ngOnInit(){

    await ensureSwiperRegistered();

    this.activatedRoute.queryParams
      .pipe(
        switchMap(queryParams => {
          if (queryParams['shelfOwner'])
            this.userName = queryParams['shelfOwner']

          return this.activatedRoute.params;
        }),
        switchMap(params => {
          if (params['id']) {
            this.userId = params['id'];
            this.libraryOwner =
              this.currentUser?.uniqeName == this.userId ||
              this.currentUser?.id == this.userId ||
              this.currentUser?.id == this.userId;

            this.booksWithoutReview$ = this.shelvesAggrigatorService.getGuestBooksWithoutReview(this.userId)
              .pipe(map(res => !res.hasError ? res.result?.items : []));
          }

          this.defaultShelves$ = this.shelvesAggrigatorService.getGuestDefaultShelvesWithBooks(this.userId, this.defaultBooksTermFilter)
            .pipe(
              switchMap(res => of(!res.hasError ? res.result?.items : [])),
              map(shelves => shelves.filter(shelf => shelf.title != 'WantToBuy')),
              tap(defaultShelves => defaultShelves.forEach(shelf => this.totalBooksCount += shelf.booksCount!))
            );

          return this.handleUserShelvesData()
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(res => {
        this.loading = false;
        if (res.hasError && res.result) return;
      });
  }

  searchBook(text: string) {
    this.router.navigate([this.routes.library_book, this.userId], { queryParams: { q: text } });
  }

  addNewShelf() {
    if (!this._localRepository.IsInBrowser) return;

    let source$: Observable<any>;

    if (this._localRepository.IsInMobile) {
      source$ = this._bottomSheet.open(NewShelfSheetComponent).afterDismissed();
    } else {
      source$ = this._dialog.open(NewShelfSheetComponent, { panelClass: 'create-shelf-dialog' }).afterClosed();
    }

    source$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        if (res?.hasError || !res) return;

        const createdShelf: ShelfItemModel = new ShelfItemModel();
        createdShelf.title = res.title;
        createdShelf.id = res.id;
        createdShelf.descriptions = res.descriptions;
        this.userShelves.push(createdShelf);
      });
  }

  loadMoreUserShelves() {
    if (!this.userHasMoreShelves) return;

    this.loadingMoreShelves = true;
    this.userShelvesSearchFilter.pgNumber += 1;
    this.shelvesAggrigatorService.getGuestShelvesWithFilter(this.userId, this.userShelvesSearchFilter)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        this.loadingMoreShelves = false;

        if (res.hasError) return;
        this.userShelves.push(...res.result?.items);
        this.userHasMoreShelves = res.result?.hasNextPage;
      });
  }

  filterShelves(value: string) {
    this.sortFilter = value;
    this.handleShelvesFilter();
  }

  setFilterDirection() {
    this.userShelvesSearchFilter.pgNumber = 0;
    this.filterDirection === 0 ? this.filterDirection = 1 : this.filterDirection = 0;
    this.handleShelvesFilter();
  }

  private handleShelvesFilter() {
    this.userShelvesSearchFilter.pgNumber = 0;
    this.userShelvesSearchFilter.orderFilters = [
      {
        propertyName: this.sortFilter,
        direction: this.filterDirection
      }
    ];

    this.handleUserShelvesData().subscribe();
  }

  private handleUserShelvesData(): Observable<TServiceResult<PageList<ShelfItemModel>>> {
    return this.shelvesAggrigatorService.getGuestShelvesWithFilter(this.userId, this.userShelvesSearchFilter)
      .pipe(
        map(res => {
          this.userShelves = res.result?.items;
          this.userHasMoreShelves = res.result?.hasNextPage;
          return res;
        })
      )
  }
}
